
import { Component, Watch, Mixins } from 'vue-property-decorator';
import Info from '@/features/shipment/components/print/Info.vue';
import PrintLabel from '@/features/shipment/components/print/PrintLabel.vue';
import DoneToDashboardFooter from '@/features/common/components/DoneToDashboardFooter.vue';
import {
  PackageItem,
} from '@/features/shipment/types';
import { getShipmentPrintLabelSrc } from '@/features/shipment/services/shipment.services';
import { getPrintNotifications } from '@/features/notifications/helpers/print';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { defaultInProgressData } from '@/features/common/raw';
import { deleteShipment } from '@/features/shipment/helpers/createShipment';
import { showSuccessAlertDialog } from '@/features/common/helpers';
import WhiteLabelMixin from '@/features/common/mixins/whiteLabelMixin';
import { NotificationData } from '@/features/settings/types';
import CreateReturnOptions from './CreateReturnOptions.vue';
import DialogSchedule from './DialogSchedule.vue';
import PrintMixin from './mixin';

@Component({
  components: {
    Info,
    PrintLabel,
    DialogSchedule,
    DoneToDashboardFooter,
    CreateReturnOptions,
  },
})
export default class PrintCard extends Mixins(WhiteLabelMixin, PrintMixin) {
  private minimumPackagingAgree = false;

  private cachedPackages = [];

  private showReturnOptions = false;

  get packages(): Array<PackageItem> {
    return this.$store.getters.getPackageSummary;
  }

  get isNotificationPrefsLoaded(): number {
    return this.$store.getters.getIsNotificationPrefs;
  }

  get isNotification(): boolean {
    return this.$store.state.notification;
  }

  get notificationData(): Array<NotificationData> {
    return this.$store.state.notificationData;
  }

  @Watch('packages', { immediate: true })
  private cachePackages(newVal) {
    if (!newVal || !newVal.length) return;
    this.cachedPackages = newVal;
  }

  @Watch('isNotification')
  private printAfterNotifications(value: boolean) {
    if (value) return;
    this.printLabel();
  }

  @Watch('isPrintDataLoaded')
  @Watch('isNotificationPrefsLoaded', { immediate: true })
  private loadNotifications(newValue) {
    if (!newValue) return;

    getPrintNotifications(this.packages);
    this.minimumPackagingAgree = !this.notificationData.some((el) => el.name === 'MinimumPackaging');

    if (!this.minimumPackagingAgree) {
      this.subscribeNotificationSubmit();
    }

    this.printLabel();
  }

  destroyed(): void {
    eventBus.$off(eventNames.NOTIFICATION_SUBMIT, this.minimumPackagingNotificationSubmit);
    this.$store.commit('setNotification', false);
  }

  private subscribeNotificationSubmit() {
    eventBus.$once(eventNames.NOTIFICATION_SUBMIT, this.minimumPackagingNotificationSubmit);
  }

  private printLabel() {
    const canPrint = this.isPrintDataLoaded
      && !this.isNotification
      && this.minimumPackagingAgree
      && this.isNotificationPrefsLoaded;

    if (canPrint) {
      this.getPrintLabelSrcAndPrint();
    }
  }

  private async viewLabelInNewTab() {
    if (!this.secureshipTrackingNo) {
      this.showTrackingNumberInvalidWarning();
      return;
    }

    const pdfSrc = await getShipmentPrintLabelSrc(
      this.secureshipTrackingNo, this.currentLabelSize,
    );

    if (!pdfSrc) return;

    window.open(pdfSrc, '_blank');
  }

  private cancelShipment() {
    if (!this.secureshipTrackingNo) {
      this.showTrackingNumberInvalidWarning();
      return;
    }

    const onSuccess = () => {
      showSuccessAlertDialog(this.$t('shipment.alertDialog.shipmentCanceled') as string);
      this.$store.commit('setInProgress', { ...defaultInProgressData, packages: [] });
      this.$router.replace({ name: 'shipment-create' });
    };

    deleteShipment(
      this.secureshipTrackingNo,
      onSuccess,
      {
        content: this.$t('shipment.cancelShipmentPrompt') as string,
        btnText: this.$t('buttons.cancelShipment') as string,
      },
    );
  }

  private minimumPackagingNotificationSubmit() {
    this.minimumPackagingAgree = true;
    this.printLabel();
  }

  private reShowNotifications() {
    getPrintNotifications(this.cachedPackages);
    this.subscribeNotificationSubmit();
  }

  private handlePrintClick() {
    if (this.minimumPackagingAgree) {
      this.printLabel();
    } else {
      this.reShowNotifications();
    }
  }
}
